/* eslint-disable max-lines-per-function */
import { CompleteAddress } from '@dreryk/address-common-api';
import {
   CurrentHealthcarePracticeUpdateCommand,
   HealthcarePracticeUpdateResult,
   ServiceProvidingLocationUpdateDetails,
   ServiceProvidingLocationUpdateDetailsData,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { P1IntegrationCertificatesValidationWarning } from '@dreryk/egabinet-p1-frontend-api';
import { Regon } from '@dreryk/regon-common-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { dispatchSnackbarPositiveEvent, dispatchSnackbarWarningEvent } from '@dreryk/react-components';
import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { Choice, Slot } from '@dreryk/watermelon';

import { healthcarePracticeTranslationKeys } from '../../../translations';

import { HealthcarePracticeUpdateFormBean } from './HealthcarePracticeUpdateForm.types';

const resolveValidationWarningCodes = (validationWarningCodes: P1IntegrationCertificatesValidationWarning[]): void => {
   return validationWarningCodes.forEach((validationWarningCode) => {
      return dispatchSnackbarWarningEvent(
         healthcarePracticeTranslationKeys.validationWarningCodes[validationWarningCode],
         {
            autoHideDelayInMs: false,
         }
      );
   });
};

export const useCurrentHealthcarePracticeUpdateCommand = (
   onUpdate: () => Promise<void>
): UseCqrsCommandResult<HealthcarePracticeUpdateResult, HealthcarePracticeUpdateFormBean> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new CurrentHealthcarePracticeUpdateCommand();
         // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
         command.registerBookNumber = parameters.registerBookNumber!;
         command.medicalChamberId = parameters.medicalChamber!.id;
         command.name = parameters.name!;
         command.phoneNumber = parameters.phoneNumber!;
         command.emailAddress = Choice.withUseDefaultIfNull(parameters.emailAddress);
         command.taxIdentifier = TaxIdentifier.of(parameters.taxIdentifier!);
         command.regon = Regon.of(parameters.regon!);
         command.address = CompleteAddress.builder()
            .withTown(parameters.city!)
            .withPostcode(parameters.postcode!)
            .withStreet(parameters.street)
            .withBuilding(parameters.house!)
            .withFlat(parameters.flat)
            .build();
         command.skipAddressInStamp = parameters.skipAddressInStamp;

         if (parameters.logo) {
            command.logo = parameters.logo;
         }

         if (parameters.serviceProvidingLocationsToUpdate) {
            command.serviceProvidingLocations = Slot.of(
               parameters.serviceProvidingLocationsToUpdate.map((serviceProvidingLocation) => {
                  const serviceProvidingLocationUpdateDetails: ServiceProvidingLocationUpdateDetails = {
                     locationCode: serviceProvidingLocation.locationCode,
                  };

                  if (!serviceProvidingLocation.usingHealthcarePracticeDetails) {
                     const serviceProvidingLocationUpdateDetailsData: ServiceProvidingLocationUpdateDetailsData = {
                        name: serviceProvidingLocation.serviceProvidingLocationName!,
                        phoneNumber: serviceProvidingLocation.phoneNumber!,
                        address: CompleteAddress.builder()
                           .withTown(serviceProvidingLocation.city!)
                           .withPostcode(serviceProvidingLocation.postcode!)
                           .withStreet(serviceProvidingLocation.street)
                           .withBuilding(serviceProvidingLocation.house!)
                           .withFlat(serviceProvidingLocation.flat)
                           .build(),
                     };

                     if (serviceProvidingLocation.emailAddress) {
                        serviceProvidingLocationUpdateDetailsData.emailAddress = Choice.of(
                           serviceProvidingLocation.emailAddress
                        );
                     }

                     serviceProvidingLocationUpdateDetails.serviceProvidingLocationUpdateDetailsData = Choice.of(
                        serviceProvidingLocationUpdateDetailsData
                     );
                  }

                  return serviceProvidingLocationUpdateDetails;
               })
            );
         }

         return command;
      },
      {
         onSuccess: (result) => {
            dispatchSnackbarPositiveEvent(healthcarePracticeTranslationKeys.updatePracticeDataSuccess);
            resolveValidationWarningCodes(
               result.validationWarningCodes as P1IntegrationCertificatesValidationWarning[]
            );
            onUpdate();
         },
      }
   );
};
